import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

/**
 * Motion tiers (R14/R15): Default (heavy — the default everywhere) and
 * Work Mode ("engines idling" — near-instant functional motion, ambient
 * off, auto-theatrics suppressed). A body class swaps the tier-scaled
 * --dur-* tokens; components that orchestrate theatrics read the tier
 * via useTier(). prefers-reduced-motion clamps the effective tier to
 * Work regardless of the stored choice (R23).
 */
export type MotionTier = 'default' | 'work'

const STORAGE_KEY = 'v2:tier'
const WORK_CLASS = 'tier-work'

export function getStoredTier(): MotionTier {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'work' ? 'work' : 'default'
  } catch {
    return 'default'
  }
}

export function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

function applyTierClass(tier: MotionTier, prm: boolean) {
  const effective: MotionTier = prm ? 'work' : tier
  document.body.classList.toggle(WORK_CLASS, effective === 'work')
}

/**
 * Pre-render V2 bootstrap (no-flash, same pattern as the density
 * bootstrap): marks the document as V2, pins the dark theme (V2 is
 * dark-only — V1's ThemeToggle/light theme are not mounted), and
 * applies the PRM-clamped tier class.
 */
export function bootstrapV2() {
  document.documentElement.setAttribute('data-theme', 'dark')
  document.body.classList.add('hubv2')
  applyTierClass(getStoredTier(), prefersReducedMotion())
}

interface TierContextValue {
  /** The user's chosen tier (persisted per device under v2:tier). */
  tier: MotionTier
  /** PRM-clamped tier — what the UI actually runs (R23). */
  effectiveTier: MotionTier
  /** effectiveTier === 'work' */
  isWork: boolean
  /** True when PRM forces work-level restraint over a 'default' choice. */
  prmClamped: boolean
  setTier: (t: MotionTier) => void
  toggleTier: () => void
}

const TierContext = createContext<TierContextValue | null>(null)

export function TierProvider({ children }: { children: ReactNode }) {
  const [tier, setTier] = useState<MotionTier>(getStoredTier)
  const [prm, setPrm] = useState<boolean>(prefersReducedMotion)

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const onChange = () => setPrm(mq.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  useEffect(() => {
    applyTierClass(tier, prm)
    try {
      window.localStorage.setItem(STORAGE_KEY, tier)
    } catch {
      /* private mode / quota — tier just won't persist */
    }
  }, [tier, prm])

  const value = useMemo<TierContextValue>(() => {
    const effectiveTier: MotionTier = prm ? 'work' : tier
    return {
      tier,
      effectiveTier,
      isWork: effectiveTier === 'work',
      prmClamped: prm && tier === 'default',
      setTier,
      toggleTier: () => setTier((t) => (t === 'work' ? 'default' : 'work')),
    }
  }, [tier, prm])

  return <TierContext.Provider value={value}>{children}</TierContext.Provider>
}

export function useTier(): TierContextValue {
  const ctx = useContext(TierContext)
  if (!ctx) throw new Error('useTier must be used inside TierProvider')
  return ctx
}
